"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ConfirmationController,
  ConfirmationOptions,
  ConfirmationState,
} from "./types";
import type { CopilotPermission } from "../types";

type CtxValue = {
  state: ConfirmationState;
  controller: ConfirmationController;
  resolve: (ok: boolean) => void;
  /** Llamado por CopilotProvider con settings.permission */
  setPermission: (p: CopilotPermission) => void;
};

const ConfirmationContext = createContext<CtxValue | null>(null);

export function ConfirmationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<ConfirmationState>({
    open: false,
    options: null,
  });
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);
  const permissionRef = useRef<CopilotPermission>("ask");

  const setPermission = useCallback((p: CopilotPermission) => {
    permissionRef.current = p;
  }, []);

  const resolve = useCallback((ok: boolean) => {
    setState({ open: false, options: null });
    const r = resolverRef.current;
    resolverRef.current = null;
    r?.(ok);
  }, []);

  const controller = useMemo<ConfirmationController>(
    () => ({
      confirm(opts: ConfirmationOptions) {
        const permission = permissionRef.current;
        const isDanger = !!opts.danger;

        // Bypass total — nunca pregunta
        if (permission === "bypass") {
          return Promise.resolve(true);
        }
        // Accept edits — solo pregunta en acciones peligrosas
        if (permission === "accept-edits" && !isDanger) {
          return Promise.resolve(true);
        }

        // Default: muestra el modal
        return new Promise<boolean>((res) => {
          // Si hay otra confirmación pendiente, la cancelamos primero
          if (resolverRef.current) {
            resolverRef.current(false);
          }
          resolverRef.current = res;
          setState({ open: true, options: opts });
        });
      },
    }),
    [],
  );

  const value = useMemo<CtxValue>(
    () => ({ state, controller, resolve, setPermission }),
    [state, controller, resolve, setPermission],
  );

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
    </ConfirmationContext.Provider>
  );
}

export function useConfirmationInternal() {
  const ctx = useContext(ConfirmationContext);
  if (!ctx) {
    throw new Error(
      "useConfirmationInternal debe usarse dentro de ConfirmationProvider",
    );
  }
  return ctx;
}
